import { useState, useEffect } from "react";
import { useAppSelector } from "../../../context-store";
import IdNameModel from "../../../models/common/IdNameModel";
import CandidateRelatedJobRequirementsGridModel from "../../../models/candidate/response/CandidateRelatedJobRequirementsGridModel";
import candidateService from "../../../services/candidate/candidate-service";
import CandidateRelatedJobRequirementsSearchInputModel from "../../../models/candidate/request/CandidateRelatedJobRequirementsSearchInputModel";
import { SortDirection } from "../../../models/enums/SortDirectionEnum";
import toasterUtils from "../../../utils/toaster-utils";
import CandidateNewJobsGridComponent from "./CandidateNewJobsGridComponent";
import CandidateAppliedJobsGridComponent from "./CandidateAppliedJobsGridComponent";
import CandidateRelatedJobRequirementRequestModel from "../../../models/candidate/request/CandidateRelatedJobRequirementRequestModel";
import masterDataService from "../../../services/master-data-service";
import modalUtils from "../../../utils/modal-utils";
import { PagingEnum } from "../../../models/enums/IAIEnum";
import PagedListModel from "../../../models/common/PagedListModel";
import { Tabs, Tab, Box, FormControl, InputLabel, Select, MenuItem, Button, Autocomplete, TextField } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { GuidEnum } from "../../../models/enums/GuidEnum";
import Alert from '@mui/material/Alert';

const CandidateJobsComponent = () => {
    let loggedInUser = useAppSelector((state) => state.authState.loggedInUser);
    const [newJobsList, setNewJobsList] = useState<CandidateRelatedJobRequirementsGridModel[]>([]);
    const [newJobsPagedList, setNewJobsPagedList] = useState<PagedListModel<CandidateRelatedJobRequirementsGridModel>>();
    const [refreshNewJobs, setRefreshNewJobs] = useState<number>(2);
    const [appliedJobsList, setAppliedJobsList] = useState<CandidateRelatedJobRequirementsGridModel[]>([]);
    const [appliedJobsPagedList, setAppliedJobsPagedList] = useState<PagedListModel<CandidateRelatedJobRequirementsGridModel>>();
    const [refreshAppliedJobs, setRefreshAppliedJobs] = useState<number>(2);
    const [primarySkills, setPrimarySkills] = useState<IdNameModel[]>([]);
    const [companies, setCompanies] = useState<IdNameModel[]>([]);
    const [designations, setDesignations] = useState<IdNameModel[]>([]);
    const [primarySkillSearch, setPrimarySkillSearch] = useState<number>(0);
    const [companySearch, setCompanySearch] = useState<string>("");
    const [designationSearch, setDesignationSearch] = useState<string>("");
    const [page, setPage] = useState<number>(PagingEnum.page);
    const [pageSize, setPageSize] = useState<number>(PagingEnum.pageSize);
    const [sortColumn, setSortColumn] = useState<string>("JobCode");
    const [sortDirection, setSortDirection] = useState<number>(PagingEnum.sortDirectionDesc);
    const [pageSizesList, setPageSizesList] = useState<number[]>([]);
    const [pageApplied, setPageApplied] = useState<number>(PagingEnum.page);
    const [pageSizeApplied, setPageSizeApplied] = useState<number>(PagingEnum.pageSize);
    const [tabIndex, setTabIndex] = useState(0);

    useEffect(() => {
        loadPageSizes();
        loadCompanies();
        loadPrimarySkills();
        checkCandidateHasSchedule();
    }, []);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabIndex(newValue);
    };

    const checkCandidateHasSchedule = () => {
        if (loggedInUser?.profileId) {
            candidateService.checkIfCandidateHasSchedule(loggedInUser?.profileId).then((res) => {
                if (res.isSuccess && res.data) {
                    getNewJobs(getSearchFilters(page, pageSize, sortColumn, sortDirection));
                    getAppliedJobs(getSearchFiltersApplied(pageApplied, pageSizeApplied, sortColumn, sortDirection));
                }
            }).catch((error) => {
                toasterUtils.showError("Erorr fetching Primary skills");
            });
        }
    };

    const loadPrimarySkills = () => {
        masterDataService.loadPrimarySkills().then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setPrimarySkills(res.items);
        }).catch((error) => {
            toasterUtils.showError("Erorr fetching Primary skills");
        });
    };

    const loadCompanies = () => {
        masterDataService.loadCompanies().then((res) => {
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            setCompanies(res.items);
        }).catch((error) => {
            toasterUtils.showError("Erorr fetching Primary skills");
        });
    };

    const loadDesignations = (companyId: string) => {
        setCompanySearch(companyId);
        if (companyId !== "") {
            masterDataService.loadDesignation(companyId).then((res) => {
                if (!res.isSuccess || res.errorMessages.length > 0) {
                    toasterUtils.showError(res.errorMessages.toString());
                    return;
                }
                setDesignations(res.items);
            }).catch((error) => {
                toasterUtils.showError("Erorr fetching Primary skills");
            });
        } else {
            setDesignations([]);
        }
    };

    const loadPageSizes = () => {
        masterDataService.loadPageSizesForPaging().then((res) => {
            setPageSizesList(res);
        }).catch((error) => {
            toasterUtils.showError(error);
        });
    };

    const getSearchFilters = (page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: CandidateRelatedJobRequirementsSearchInputModel = {
            candidateId: "",
            designationId: designationSearch === "" ? null : designationSearch,
            companyId: companySearch === "" ? null : companySearch,
            primaySkillId: primarySkillSearch === 0 ? null : primarySkillSearch,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getSearchFiltersApplied = (page: number, pageSize: number, sortColumn: string, sortDirection: number) => {
        var inputData: CandidateRelatedJobRequirementsSearchInputModel = {
            candidateId: "",
            designationId: null,
            companyId: null,
            primaySkillId: null,
            page: page,
            pageSize: pageSize,
            sort: sortColumn,
            sortDir: sortDirection
        };
        return inputData;
    };

    const getNewJobs = (inputData: CandidateRelatedJobRequirementsSearchInputModel) => {
        if (loggedInUser?.profileId) {
            setRefreshNewJobs(2);
            inputData.candidateId = loggedInUser?.profileId;
            candidateService.getCandidateRelatedJobRequirement(inputData).then((res) => {
                setNewJobsList([]);
                setNewJobsList(res.items);
                setNewJobsPagedList(res);
                setRefreshNewJobs(refreshNewJobs * 5);
            }).catch((error) => {
                toasterUtils.showError(error);
            });
        } else {
            setRefreshNewJobs(refreshNewJobs * 5);
        }
    };

    const getAppliedJobs = (inputData: CandidateRelatedJobRequirementsSearchInputModel) => {
        if (loggedInUser?.profileId) {
            setRefreshAppliedJobs(2);
            inputData.candidateId = loggedInUser?.profileId;
            modalUtils.showLoader();
            candidateService.getCandidateAppliedJobRequirements(inputData).then((res) => {
                modalUtils.closeModal();
                setAppliedJobsList([]);
                setAppliedJobsPagedList(res);
                setAppliedJobsList(res.items);
                setRefreshAppliedJobs(refreshNewJobs * 5);
            }).catch((error) => {
                modalUtils.closeModal();
                toasterUtils.showError(error);
            });
        } else {
            setRefreshAppliedJobs(refreshNewJobs * 5);
        }
    };

    const onApplyActionHandler = (row: CandidateRelatedJobRequirementRequestModel) => {
        modalUtils.showLoader();
        candidateService.saveCandidateJobApplication(row).then((res) => {
            modalUtils.closeModal();
            if (!res.isSuccess || res.errorMessages.length > 0) {
                toasterUtils.showError(res.errorMessages.toString());
                return;
            }
            toasterUtils.showSuccess(res.informationMessages.toString());
            getNewJobs(getSearchFilters(page, pageSize, sortColumn, sortDirection));
            getAppliedJobs(getSearchFiltersApplied(pageApplied, pageSizeApplied, sortColumn, sortDirection));
        }).catch((error) => {
            modalUtils.closeModal();
            toasterUtils.showError(error);
        });
    };

    const onPageChangeActionHandler = (newPage: number) => {
        setPage(newPage);
        getNewJobs(getSearchFilters(newPage, pageSize, sortColumn, sortDirection));
    };

    const onPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSize(newPageSize);
        setPage(PagingEnum.page);
        getNewJobs(getSearchFilters(PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    const onAppliedPageChangeActionHandler = (newPage: number) => {
        setPageApplied(newPage);
        getAppliedJobs(getSearchFiltersApplied(newPage, pageSizeApplied, sortColumn, sortDirection));
    };

    const onAppliedPageSizeChangeActionHandler = (newPageSize: number) => {
        setPageSizeApplied(newPageSize);
        setPageApplied(PagingEnum.page);
        getAppliedJobs(getSearchFiltersApplied(PagingEnum.page, newPageSize, sortColumn, sortDirection));
    };

    return (
        <div className="card card-white">
            <div className="card-body">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={tabIndex} onChange={handleTabChange} aria-label="candidate jobs tabs">
                        <Tab label="NEW JOBS" />
                        <Tab label="APPLIED JOBS" />
                    </Tabs>
                </Box>
                <Box sx={{ mt: 2 }}>
                    {tabIndex === 0 && (
                        <div className="row">
                            <div className="col-md-12">
                                {refreshNewJobs > 2 && newJobsList.length > 0 && <>
                                    <div className="apply-filter">
                                        Filter Jobs <i className="fas fa-filter" data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight" aria-controls="offcanvasRight"></i>
                                    </div>
                                    <div className="offcanvas offcanvas-end" id="offcanvasRight" aria-labelledby="offcanvasRightLabel">
                                        <div className="offcanvas-header">
                                            <h5 className="offcanvas-title" id="offcanvasRightLabel">Apply Filter</h5>
                                            <a type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></a>
                                        </div>
                                        <div className="offcanvas-body">
                                            <div className="row">
                                                <div>
                                                    <FormControl fullWidth size="small" sx={{ mb: 2 }} defaultValue={0}>
                                                        <Autocomplete options={primarySkills} value={primarySkills.find((x) => x.id === primarySkillSearch) || null}
                                                            onChange={(_, newValue) => setPrimarySkillSearch(newValue ? newValue.id : 0)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            renderInput={(params) => (
                                                                <TextField {...params} label="Primary Skill" size="small" margin="normal" fullWidth />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <div>
                                                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                                        <Autocomplete options={companies} value={companies.find((x) => x.guId === companySearch) || null}
                                                            onChange={(_, newValue) => loadDesignations(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                                            renderInput={(params) => (
                                                                <TextField {...params} label="Company" size="small" margin="normal" fullWidth />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <div>
                                                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                                        <Autocomplete options={designations} value={designations.find((x) => x.guId === designationSearch) || null}
                                                            onChange={(_, newValue) => setDesignationSearch(newValue ? newValue.guId : GuidEnum.Empty)} getOptionLabel={(option) => option?.name || ""} isOptionEqualToValue={(option, value) => option.guId === value.guId}
                                                            renderInput={(params) => (
                                                                <TextField {...params} label="Designation" size="small" margin="normal" fullWidth />
                                                            )}
                                                        />
                                                    </FormControl>
                                                </div>
                                                <div className="text-center">
                                                    <Button variant="contained" color="primary" type="submit"
                                                        onClick={() => getNewJobs(getSearchFilters(page, pageSize, sortColumn, sortDirection))} startIcon={<SearchIcon />}>
                                                        Search
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>}
                            </div>
                            <div className="col-md-12">
                                {newJobsList.length < 1 &&
                                    <Alert severity="info">No matching Jobs found.</Alert>
                                }
                                {refreshNewJobs > 2 && newJobsList.length > 0 &&
                                    <CandidateNewJobsGridComponent
                                        key={refreshNewJobs}
                                        requirementList={newJobsList}
                                        totalSize={newJobsPagedList?.totalCount ? newJobsPagedList?.totalCount : newJobsList.length}
                                        currentPage={page + 1}
                                        pagesize={pageSize}
                                        sortColumn={sortColumn}
                                        sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                                        pageSizesList={pageSizesList}
                                        onApply={onApplyActionHandler}
                                        onPageChangeAction={onPageChangeActionHandler}
                                        onPageSizeChangeAction={onPageSizeChangeActionHandler} />
                                }
                            </div>
                        </div>
                    )}
                    {tabIndex === 1 && (
                        <div>
                            {appliedJobsList.length < 1 &&
                                <Alert severity="info">No Jobs applied.</Alert>
                            }
                            {refreshAppliedJobs > 2 &&
                                <CandidateAppliedJobsGridComponent
                                    key={refreshAppliedJobs}
                                    requirementList={newJobsList}
                                    totalSize={appliedJobsPagedList?.totalCount ? appliedJobsPagedList?.totalCount : appliedJobsList?.length}
                                    currentPage={pageApplied + 1}
                                    pagesize={pageSizeApplied}
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection === SortDirection.Asc ? "asc" : "desc"}
                                    pageSizesList={pageSizesList}
                                    onPageChangeAction={onAppliedPageChangeActionHandler}
                                    onPageSizeChangeAction={onAppliedPageSizeChangeActionHandler} />
                            }
                        </div>
                    )}
                </Box>
            </div>
        </div>
    );
};
export default CandidateJobsComponent;